/**
 * ADR-0185 Phase A — pure composition behind the graded change sheet: the
 * Change column's intent prose (built from the served facts, bold segments
 * marked), the Status column's wording per self-check state, and the header
 * count line. No DOM, no fetches — unit-tested in sheetMath.test.ts.
 *
 * The sheet's vocabulary is the owner's (design record 2026-09-04): "Plex
 * schedule" names the imported plan on every rendered surface; grade ①
 * (Phase B) is the planner's own hand move, ② directly impacted, ③
 * downstream.
 */
import type { ChangeSheet, ChangeSheetRow } from "../../boardTypes.js";
import { ordinal } from "./laneMath.js";
import { shortWhen } from "./refineMath.js";

/** One run of Change-column text; `bold` marks the value the eye should catch. */
export interface ChangeSegment {
  text: string;
  bold?: boolean;
}

/** "Sat 9/6" — the short day form the mockup's Change column uses. */
export function shortDay(iso: string): string {
  const d = new Date(iso);
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  return `${weekday} ${d.getMonth() + 1}/${d.getDate()}`;
}

function hoursBetween(fromIso: string | null, toIso: string | null): number | null {
  if (!fromIso || !toIso) return null;
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return (to - from) / 3_600_000;
}

/**
 * The Change column: one main clause naming what the optimized plan does with
 * the job, then the changeover repricing when it changed, then the promise
 * consequence. Clause priority mirrors the grading: a machine move leads,
 * then a near-window pull/push, then a reorder, then a plain slide.
 */
export function changeSummary(row: ChangeSheetRow): ChangeSegment[] {
  const parts: ChangeSegment[] = [];
  const p = row.grade === 1 ? row.placement : null;
  const reordered = row.rank_from != null && row.rank_to != null && row.rank_from !== row.rank_to;

  if (p) {
    // ① — the planner's own intent leads, in their words.
    if (row.machine_changed) {
      parts.push({ text: "moves to " }, { text: row.machine_to, bold: true }, { text: " — your move" });
      if (reordered) parts.push({ text: `, runs ${ordinal(row.rank_to!)} there` });
    } else if (p.kind === "exact_time" && row.to_start) {
      parts.push({ text: "starts " }, { text: shortWhen(row.to_start), bold: true }, { text: " — your time" });
    } else if (p.kind === "near_time" && row.to_start) {
      parts.push({ text: "starts " }, { text: shortWhen(row.to_start), bold: true }, { text: " — near your date" });
    } else if (reordered) {
      parts.push({ text: "runs " }, { text: ordinal(row.rank_to!), bold: true }, { text: ` (was ${ordinal(row.rank_from!)})` });
      if (p.kind === "run_next") parts.push({ text: " — run next" });
      else if (p.kind === "before_after" && p.anchor_job) parts.push({ text: ` — ${p.side === "before" ? "before" : "after"} ${p.anchor_job}` });
    } else if (row.rank_to != null) {
      parts.push({ text: `stays ${ordinal(row.rank_to)} — already where Plex has it` });
    } else {
      parts.push({ text: "your move" });
    }
  } else if (row.machine_changed) {
    parts.push({ text: "moves to " }, { text: row.machine_to, bold: true });
  } else if (row.window_crossing === "in" && row.to_start) {
    parts.push({ text: "pulled up to " }, { text: shortDay(row.to_start), bold: true });
  } else if (row.window_crossing === "out" && row.to_start) {
    parts.push({ text: "pushed back to " }, { text: shortDay(row.to_start), bold: true });
  } else if (row.rank_from != null && row.rank_to != null && row.rank_from !== row.rank_to) {
    parts.push(
      { text: "runs " },
      { text: ordinal(row.rank_to), bold: true },
      { text: ` (was ${ordinal(row.rank_from)})` }
    );
  } else {
    const dh = hoursBetween(row.from_start, row.to_start);
    if (dh != null && Math.abs(dh) >= 1) {
      const rounded = Math.round(Math.abs(dh));
      parts.push({ text: `slides ${rounded} h ${dh > 0 ? "later" : "earlier"}` });
    } else {
      parts.push({ text: "timing changes" });
    }
  }

  const from = row.changeover_from?.setup_hours;
  const to = row.changeover_to?.setup_hours;
  if (typeof from === "number" && typeof to === "number" && from !== to) {
    parts.push({ text: ` — changeover ${from} → ${to} h` });
  }

  if (row.promise_change === "leaves_risk") {
    parts.push({ text: " — " }, { text: "leaves the at-risk list", bold: true });
  } else if (row.promise_change === "enters_risk") {
    parts.push({ text: " — " }, { text: "puts the promise at risk", bold: true });
  }

  return parts;
}

export type SheetStatusTone = "done" | "near" | "wait" | "auto";

/** The Status column, per grade and self-check state (① and ② share the check). */
export function sheetStatus(
  row: Pick<ChangeSheetRow, "grade" | "check">,
  liveCheckAvailable: boolean
): { text: string; tone: SheetStatusTone } {
  if (row.grade === 3) return { text: "Plex replans this", tone: "auto" };
  if (!liveCheckAvailable || !row.check) return { text: "not checked", tone: "wait" };
  switch (row.check.state) {
    case "showing":
      return { text: "✓ showing in Plex", tone: "done" };
    case "near":
      return { text: "≈ in Plex, within the hour", tone: "near" };
    default:
      return { text: "not yet in Plex", tone: "wait" };
  }
}

/**
 * The header's middle chunk: the self-check tally when the live book was
 * readable, the honest line when it wasn't.
 */
export function sheetCountLine(sheet: Pick<ChangeSheet, "needing_action" | "showing_count" | "live_check">): string {
  if (!sheet.live_check.available) return "couldn't read the live Plex schedule just now";
  return `${sheet.showing_count} of ${sheet.needing_action} entries needing action showing in Plex`;
}

/** The Machine column: "C7/H21 → H38" on a move, the one machine otherwise. */
export function machineCell(row: Pick<ChangeSheetRow, "machine_from" | "machine_to" | "machine_changed">): string {
  return row.machine_changed && row.machine_from ? `${row.machine_from} → ${row.machine_to}` : row.machine_to;
}
